// ROUM-VENTUREGEN-SCANBTN-20260620
import React, { useState } from 'react';
import PdfEditor from '../PdfEditor';
import { getVers, autoV1, genFileName } from './gendocs';
import { ensureReal, openS3, regenDocs } from './docgen';
import { makeScannedPdf } from './scanpdf';
import { uploadDoc, fetchFileBlob } from './api';
import { UpLabel } from './stepcommon';
import type { Project, DocVerMap, DocVer } from './types';
import { saveToProject, savedMsg } from './projectFolder';

interface Props {
  p: Project;
  k: string;
  seal: boolean;
  pdfOnly?: boolean;
  onPatch: (patch: Partial<Project>) => void;
  onVer: (k: string) => void;
  onPv: (k: string, idx?: number) => void;
  toast: (m: string) => void;
}

type RepKind = 'edit' | 'seal' | 'pdfonly';

export default function GenDocRow({ p, k, seal, pdfOnly, onPatch, onVer, onPv, toast }: Props) {
  const [busy, setBusy] = useState('');
  const [more, setMore] = useState(false);
  const [editUrl, setEditUrl] = useState<string | null>(null);
  const [askRegen, setAskRegen] = useState(false);
  const vers = getVers(p, k);
  const cur = vers[vers.length - 1];
  const real = !!(cur.s3doc || cur.s3pdf);

  const dl = async (fmt: 'doc' | 'pdf') => {
    if (busy) return;
    setBusy(fmt);
    // 조합 저장 폴더로 — 처음 한 번만 폴더를 고르고 다음부턴 바로(2026-11-01 지도사) · 폴더를 먼저 정한 뒤 파일 준비
    const name = genFileName(p, k, fmt === 'doc' ? 'docx' : 'pdf');
    try {
      const folder = await saveToProject(p.id, name, async () => {
        const v = await ensureReal(p, k, onPatch, toast);
        const key = fmt === 'doc' ? v.s3doc : v.s3pdf;
        if (!key) throw new Error(fmt === 'doc' ? 'Word 파일이 없습니다' : 'PDF 변환에 실패한 버전입니다 — 재생성하거나 PDF를 직접 올려주세요');
        return fetchFileBlob(key, name);
      });
      toast(savedMsg(folder, name));
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };

  // 🖨 스캔본: 현재 PDF(날인본 있으면 날인본=도장·서명 합성됨, 없으면 원본)에 스캔효과 적용 후 다운로드
  const dlScan = async () => {
    if (busy) return;
    setBusy('scan');
    const name = genFileName(p, k, 'pdf').replace(/\.pdf$/, '') + '_스캔본.pdf';
    try {
      const folder = await saveToProject(p.id, name, async () => {
        const v = await ensureReal(p, k, onPatch, toast);
        if (!v.s3pdf) throw new Error('PDF가 없습니다 — 먼저 PDF를 생성/변환하세요');
        return makeScannedPdf(await fetchFileBlob(v.s3pdf, genFileName(p, k, 'pdf')));
      });
      toast(savedMsg(folder, name));
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };

  const replace = async (f: File, kind: RepKind) => {
    setBusy('up');
    try {
      const r = await uploadDoc(p.id, k, kind, f);
      const dv = JSON.parse(JSON.stringify(p.docver || {})) as DocVerMap;
      if (!dv[k] || !dv[k].length || (dv[k].length === 1 && dv[k][0].kind === 'auto' && !dv[k][0].s3doc)) dv[k] = [autoV1(p, k)];
      const prev = dv[k][dv[k].length - 1];
      const n = dv[k].length + 1;
      const at = new Date().toISOString();
      const ver: DocVer = kind === 'edit'
        ? { label: '수정본 v' + n, doc: r.doc, pdf: r.pdf, kind: 'edit', s3doc: r.s3doc, s3pdf: r.s3pdf, at }
        : kind === 'seal'
          ? { label: '날인본 v' + n, doc: prev.doc, pdf: r.pdf, kind: 'seal', s3doc: prev.s3doc || null, s3pdf: r.s3pdf, at }
          : { label: 'PDF만 교체 v' + n, doc: prev.doc, pdf: r.pdf, kind: 'pdfonly', s3doc: prev.s3doc || null, s3pdf: r.s3pdf, at };
      if (prev.date) ver.date = prev.date;   // 날인 · 수정은 앞 버전 날짜 그대로(등록 서류 날짜 점검 · regdate.ts)
      dv[k] = [...dv[k], ver];
      onPatch({ docver: dv });
      toast(kind === 'edit'
        ? (r.converted ? '수정본 교체됨 · PDF 자동 재변환 완료' : '⚠ 수정본 저장됨 · PDF 변환 실패 — PDF를 직접 올려주세요')
        : kind === 'seal' ? '날인본(PDF) 교체됨' : '⚠ PDF만 교체됨 · Word와 불일치 상태입니다');
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };

  // ✍ 서명: 현장에서 고객이 드로잉패드로 직접 서명 → 저장 시 래스터화(텍스트 추출 방지) PDF가 날인본으로 저장됨
  const openSign = async () => {
    if (busy) return;
    setBusy('sign');
    try {
      try { localStorage.removeItem('pdf_edit_url'); } catch (e) { /* 이전 취소분 잔존 방지 */ }
      const v = await ensureReal(p, k, onPatch, toast);
      if (!v.s3pdf) { toast('PDF가 없습니다 — 먼저 PDF를 생성/변환하세요'); return; }
      const blob = await fetchFileBlob(v.s3pdf, genFileName(p, k, 'pdf'));
      setEditUrl(URL.createObjectURL(blob));
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  // ↻ 재생성 (2026-10-09 지도사 「건건이 재생성 버튼」) — 지금 마스터 · 신청일로 이 문서 하나만 다시 만든다(새 버전 「재생성 vN」).
  //   서명 완료본 · 수정본 위에 덮을 때는 한 번 더 눌러 확인(브라우저 확인창 없이 버튼 문구로) · 이전 버전은 이력에 그대로 남는다
  const regen = async () => {
    if (busy) return;
    const keep = cur.kind === 'seal' || cur.kind === 'edit' || cur.kind === 'pdfonly';
    if (keep && !askRegen) { setAskRegen(true); return; }
    setAskRegen(false);
    setBusy('regen');
    try { await regenDocs(p, [k], onPatch, toast); } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  const regenLabel = busy === 'regen' ? '생성 중…' : askRegen ? (cur.kind === 'seal' ? '서명본 위에 다시 만들기' : '수정본 위에 다시 만들기') : '↻ 재생성';
  const regenTitle = askRegen ? '한 번 더 누르면 지금 입력값으로 새로 만듭니다 — 서명 · 수정본은 이력에 남습니다' : '지금 마스터 정보 · 신청일로 이 문서만 다시 만들기(새 버전)';
  const regenBtn = (
    <button className={'rbtn' + (askRegen ? ' warn' : '')} disabled={!!busy && busy !== 'regen'} onClick={regen} onBlur={() => setAskRegen(false)} title={regenTitle}>{regenLabel}</button>
  );
  const closeEditor = () => { if (editUrl) URL.revokeObjectURL(editUrl); setEditUrl(null); };
  const editorModal = editUrl ? (
    <PdfEditor
      fileUrl={editUrl}
      onSaveBlob={async (blob: Blob) => { await replace(new File([blob], genFileName(p, k, 'pdf'), { type: 'application/pdf' }), 'seal'); }}
      onSaved={closeEditor}
      onClose={closeEditor}
    />
  ) : null;

  if (pdfOnly) {
    return (
      <>
        <button className="histtop" onClick={() => onVer(k)} title="버전 이력">🕘 이력 {vers.length}</button>
        <div className="s3file pdf">
          <span className="rf">📕 {genFileName(p, k, 'pdf')}</span>
          {real ? <span className="pill p-green">실파일</span> : <span className="pill p-gray">미생성 · 버튼 클릭 시 생성</span>}
          <span className="grow" />
          <button className="rbtn" onClick={() => onPv(k)}>👁 미리보기</button>
          <button className="rbtn" disabled={!!busy} onClick={() => dl('pdf')}>{busy === 'pdf' ? '생성 중…' : '⬇ 원본'}</button>
          <button className="rbtn" disabled={!!busy} onClick={dlScan} title="현재 PDF에 스캔효과 적용 후 다운로드(날인본 있으면 도장·서명 포함)">{busy === 'scan' ? '스캔 중…' : '⬇ 스캔'}</button>
          <button className="rbtn" disabled={!!busy} onClick={openSign} title="고객이 드로잉패드로 직접 서명 → 날인본으로 저장">{busy === 'sign' ? '여는 중…' : '✍ 서명'}</button>
          {regenBtn}
          {more && <UpLabel text={busy === 'up' ? '업로드 중…' : '날인본 업로드(교체)'} disabled={!!busy} onUp={(f) => replace(f, 'seal')} />}
          <button className="rbtn" onClick={() => setMore((m) => !m)} title="날인본만 업로드">{more ? '✕ 닫기' : '⋯ 기타'}</button>
        </div>
        {editorModal}
      </>
    );
  }

  const lastWord = [...vers].reverse().find((v) => v.kind === 'auto' || v.kind === 'edit') || cur;
  const wordBadge = !real
    ? <span className="pill p-gray">미생성 · 버튼 클릭 시 생성</span>
    : lastWord.kind === 'auto'
      ? <span className="pill p-green">자동 생성됨</span>
      : <span className="pill p-blue">수정본 교체됨</span>;
  const needSeal = seal && cur.kind !== 'seal' ? <span className="pill p-amber">날인 필요</span> : null;
  const sealDone = cur.kind === 'seal' ? <span className="pill p-blue">✍ 서명 완료파일</span> : null;
  const mismatch = cur.kind === 'pdfonly';
  const convFail = cur.kind === 'edit' && !cur.s3pdf;

  return (
    <>
      <button className="histtop" onClick={() => onVer(k)} title="버전 이력">🕘 이력 {vers.length}</button>
      {more && (
        <div className="s3file">
          <span className="rf">📄 {genFileName(p, k, 'docx')}</span>{wordBadge}{needSeal}{sealDone}<span className="grow" />
          <button className="rbtn" disabled={!!busy} onClick={() => dl('doc')}>{busy === 'doc' ? '생성 중…' : '⬇ Word'}</button>
          <UpLabel text={busy === 'up' ? '업로드 중…' : '수정본 업로드(교체)'} disabled={!!busy} onUp={(f) => replace(f, 'edit')} />
        </div>
      )}
      <div className="s3file pdf">
        <span className="rf">📕 {genFileName(p, k, 'pdf')}</span>
        {convFail
          ? <span className="pill p-amber" title="업로드한 수정본의 PDF 변환에 실패했습니다. PDF만 교체로 직접 올려주세요.">⚠ PDF 변환 실패</span>
          : mismatch
            ? <span className="pill p-amber" title="PDF만 교체되어 Word 원본과 내용이 다를 수 있습니다. Word 수정본을 올리면 다시 일치됩니다.">⚠ Word와 불일치</span>
            : cur.kind === 'seal'
              ? <span className="pill p-blue">✍ 서명 완료파일</span>
              : real
                ? <span className="pill p-gray">PDF 자동 변환 · Word와 일치</span>
                : <span className="pill p-gray">미생성</span>}
        {needSeal}
        <span className="grow" />
        <button className="rbtn" onClick={() => onPv(k)}>👁 미리보기</button>
        <button className="rbtn" disabled={!!busy} onClick={() => dl('pdf')}>{busy === 'pdf' ? '생성 중…' : '⬇ 원본'}</button>
        <button className="rbtn" disabled={!!busy} onClick={dlScan} title="현재 PDF에 스캔효과 적용 후 다운로드(날인본 있으면 도장·서명 포함)">{busy === 'scan' ? '스캔 중…' : '⬇ 스캔'}</button>
        <button className="rbtn" disabled={!!busy} onClick={openSign} title="고객이 드로잉패드로 직접 서명 → 날인본으로 저장">{busy === 'sign' ? '여는 중…' : '✍ 서명'}</button>
        {regenBtn}
        {more && <UpLabel text={busy === 'up' ? '업로드 중…' : seal ? '날인본 업로드(PDF)' : 'PDF만 교체'} disabled={!!busy} warn={mismatch || convFail} onUp={(f) => replace(f, seal ? 'seal' : 'pdfonly')} />}
        <button className="rbtn" onClick={() => setMore((m) => !m)} title="Word·수정본·날인본 업로드">{more ? '✕ 닫기' : '⋯ 기타'}</button>
      </div>
      {editorModal}
    </>
  );
}
